/**
 * Rolling Render — brand-themed product gallery swap.
 *
 * When the RR PDP loads with ?brand=porsche (etc.), swap every
 * gallery image (hero + thumbnails) to a brand-specific version
 * from Shopify Files if the file exists. Purpose: Meta ad
 * campaigns targeting brand-loyal owners — a Porsche-targeted
 * ad landing on a page that shows a Ferrari mug kills the click.
 *
 * Extracted from sections/sipfull-product-personalised.liquid on
 * 2026-07-30 to keep that section file under Shopify's 256 KB
 * limit — the section had grown organically and was 11 KB over.
 * Loaded via <script src>; behaviour is identical to when it was
 * inline in the section.
 *
 * Filename convention (upload to Shopify → Content → Files):
 *   rolling-art-gallery-{brand}-{index}.webp
 *   e.g. rolling-art-gallery-porsche-1.webp (1-indexed, matches DOM order)
 *
 * Auto-detect: <img onerror> falls back to the default src if the
 * branded file 404s. Partial uploads (e.g. only the hero) just work
 * — unbranded slots stay as-is.
 *
 * Only fires on RR PDPs (gated by [data-rolling-art-intake] in the
 * DOM). Every other personalised product renders identically. If
 * ?brand=xxx doesn't match a known brand, nothing happens.
 */
(function () {
  'use strict';

  var VALID_BRANDS = ['porsche', 'ferrari', 'amg', 'bmw', 'audi'];

  // Only try to swap this many static gallery slots (hero + N-1 thumbs).
  // We used to blindly try 1..14 which spammed the console with 404s for
  // every non-existent branded file. Keep this in sync with what
  // brand-hero + brand-thumb files you actually intend to upload.
  var NUMBERED_THUMB_COUNT = 3;

  // Branded product videos. Unlike images (which live at a predictable
  // /cdn/shop/files/{filename} path), Shopify Files serves video assets
  // through its video CDN at /videos/c/o/v/{hash}.mp4 — the original
  // filename is discarded on upload, so we can't derive the URL from
  // the brand key. Grab the URL from Shopify → Content → Files → click
  // the file → Copy link, and paste it in below.
  //
  // A brand without an entry here simply keeps the video thumbnail
  // hidden on its landing pages (better than a wrong-brand video).
  var BRAND_VIDEOS = {
    porsche: 'https://cdn.shopify.com/videos/c/o/v/4c06e18aec4e445e8d84dafa9807cf2a.mp4',
    // ferrari: '',
    // amg:     '',
    // bmw:     '',
    // audi:    '',
  };
  var brand = '';
  try {
    brand = (new URLSearchParams(window.location.search).get('brand') || '')
      .toLowerCase().trim();
  } catch (_) { return; }
  if (!brand || VALID_BRANDS.indexOf(brand) < 0) return;
  // Only run on RR PDPs — the storefront intake snippet stamps
  // this marker on the wrapper. Every other personalised product
  // is missing it, so this block noops for them.
  if (!document.querySelector('[data-rolling-art-intake]')) return;

  // Direct Shopify CDN prefix — NOT the shop-domain alias.
  //
  // Why: '/cdn/shop/files/...' resolves to www.sipfullofdesign.com's
  // Shopify-hosted CDN alias, which caches file BODIES aggressively.
  // When you delete a file and re-upload with the same filename, the
  // shop-domain alias can keep serving the OLD content for hours.
  // The direct cdn.shopify.com path always serves the current file.
  //
  // The '/s/files/1/0809/2228/0269/files/' segment is this shop's
  // stable Shopify Files path — unchanging for the life of the store.
  // If we ever migrate stores this needs to change (grep it).
  var SHOPIFY_FILES_BASE = 'https://cdn.shopify.com/s/files/1/0809/2228/0269/files/';

  function brandedUrl(index) {
    return SHOPIFY_FILES_BASE + 'rolling-art-gallery-' + brand + '-' + index + '.webp';
  }

  // Branded video URL — pulled from the BRAND_VIDEOS map at the top of
  // the file. Returns null if this brand has no entry (in which case
  // the video slot stays hidden).
  function brandedVideoUrl() {
    return BRAND_VIDEOS[brand] || null;
  }

  // Fire-and-forget hide of every video slot in the DOM. Used both as
  // the fallback when no branded video exists, and pre-emptively at
  // start-up so the customer never sees a flash of the wrong video
  // while the HEAD-check is in flight.
  function hideVideoSlots() {
    document.querySelectorAll('.sp-thumb--video').forEach(function (t) {
      t.style.display = 'none';
    });
    document.querySelectorAll('[data-sp-hero-video]').forEach(function (v) {
      try { v.pause(); } catch (_) { /* ignore */ }
      var pane = v.closest('.fb-hero-pane') || v;
      pane.style.display = 'none';
    });
  }

  // Swap every video slot's source to the branded MP4. Reveals slots
  // that hideVideoSlots() hid pre-emptively, rewrites data-video-src
  // on the thumb button so the section's click handler promotes the
  // branded video into the hero, AND swaps the video poster (the
  // still frame shown before the customer clicks play) to the
  // branded hero image (rolling-art-gallery-{brand}-1.webp). Reusing
  // the hero as the poster saves an extra upload and keeps the video
  // thumb visually consistent with the rest of the branded gallery.
  function swapVideoSlots() {
    var url    = brandedVideoUrl();
    var poster = brandedUrl(1);      // reuse the branded hero image

    document.querySelectorAll('.sp-thumb--video').forEach(function (t) {
      t.setAttribute('data-video-src', url);
      t.setAttribute('data-video-type', 'video/mp4');
      // The video thumb has an <img> inside showing the still frame.
      // Point it at the branded hero image so the thumb no longer
      // shows the Ferrari poster.
      var img = t.querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.src = poster;
      }
      t.style.display = '';
    });
    document.querySelectorAll('[data-sp-hero-video]').forEach(function (v) {
      // Nuke any inline <source> children — they'd otherwise win over
      // the <video src> attribute the section's click handler sets.
      v.querySelectorAll('source').forEach(function (s) { s.remove(); });
      v.setAttribute('src', url);
      v.setAttribute('poster', poster);
      try { v.load(); } catch (_) { /* ignore */ }
      var pane = v.closest('.fb-hero-pane') || v;
      pane.style.display = '';
    });
    console.log('[rolling-art] Branded video wired: ' + url +
                ' (poster: ' + poster + ')');
  }

  // Probe the branded video and either wire it in or leave the slots
  // hidden. Uses a throwaway <video> element rather than a fetch HEAD
  // because Shopify's file CDN doesn't always return usable HEAD
  // responses to cross-origin fetch calls (some combinations of CORS +
  // 405-on-HEAD leave you unable to tell "file missing" from "browser
  // blocked the check"). The video element itself has no such issues —
  // it emits `loadedmetadata` on success and `error` on failure.
  function wireBrandedVideo() {
    var url = brandedVideoUrl();
    if (!url) {
      console.log('[rolling-art] No branded video mapped for ' + brand +
                  ' — slot stays hidden.');
      return;
    }
    var probe = document.createElement('video');
    probe.muted = true;
    probe.preload = 'metadata';
    probe.style.display = 'none';
    var settled = false;
    var timer = null;

    function done(success) {
      if (settled) return;
      settled = true;
      if (timer) { clearTimeout(timer); timer = null; }
      try { probe.remove(); } catch (_) { /* ignore */ }
      if (success) {
        swapVideoSlots();
      } else {
        console.log('[rolling-art] No branded video for ' + brand +
                    ' — slots stay hidden.');
      }
    }

    probe.addEventListener('loadedmetadata', function () { done(true); });
    probe.addEventListener('error',          function () { done(false); });
    // Safety net — if neither event fires within 6s (slow network,
    // weird CDN behaviour), assume the video isn't available and stay
    // hidden. Better a hidden slot than a broken player.
    timer = setTimeout(function () { done(false); }, 6000);

    probe.src = url;
    document.body.appendChild(probe);
    try { probe.load(); } catch (_) { /* ignore */ }
  }

  // Wire the swap on an <img>. Original src is preserved so onerror
  // can fall back cleanly if the branded file doesn't exist yet.
  //
  // ALSO updates the parent [data-sp-thumb] button's data-hero-src /
  // data-hero-srcset attributes — the section's click handler reads
  // those (not the <img src>) when swapping the hero image on thumb
  // tap, so without this the customer would tap a Porsche thumb and
  // get the original Ferrari image dropped into the hero.
  function swapImage(img, index) {
    if (!img || img.dataset.raBrandTried === '1') return;
    var originalSrc    = img.getAttribute('src') || '';
    var originalSrcset = img.getAttribute('srcset') || '';
    var branded        = brandedUrl(index);
    img.dataset.raBrandTried = '1';

    // Find the parent thumb button (if any) so we can rewrite the
    // data-hero-* attributes it uses when clicked. May be null if this
    // is the hero <img> itself rather than an in-thumb <img>.
    var thumbBtn = img.closest('[data-sp-thumb]');
    var originalHeroSrc    = thumbBtn ? thumbBtn.getAttribute('data-hero-src')    : null;
    var originalHeroSrcset = thumbBtn ? thumbBtn.getAttribute('data-hero-srcset') : null;

    img.onerror = function () {
      if (img.dataset.raBrandFallback === '1') return;
      img.dataset.raBrandFallback = '1';
      img.onerror = null;
      img.src = originalSrc;
      if (originalSrcset) img.setAttribute('srcset', originalSrcset);
      // Also restore the click-target data on the parent button so a
      // future tap doesn't sync a non-existent branded file into the
      // hero (browsers cache 404s aggressively).
      if (thumbBtn) {
        if (originalHeroSrc)    thumbBtn.setAttribute('data-hero-src',    originalHeroSrc);
        if (originalHeroSrcset) thumbBtn.setAttribute('data-hero-srcset', originalHeroSrcset);
      }
    };
    // Drop srcset — a single-source branded file doesn't have
    // multi-width variants, and leaving the old srcset in place
    // means the browser might still pick a Ferrari image on
    // high-DPR screens even though src points at Porsche.
    img.removeAttribute('srcset');
    img.src = branded;

    // Update the click target so tapping this thumb promotes the
    // branded URL into the hero.
    if (thumbBtn) {
      thumbBtn.setAttribute('data-hero-src', branded);
      thumbBtn.removeAttribute('data-hero-srcset');
    }
  }

  // Hero + thumbnail imgs. Both use data-sp- hooks that the parent
  // section's own JS already recognises for click-swap navigation
  // (thumb tap → hero src change). Our swap runs first, then any
  // subsequent tap on a thumb swaps its (now branded) src into
  // the hero — so brand consistency survives user interaction.
  //
  // Indexing convention (matches what the operator sees when uploading
  // branded files to Shopify Files):
  //   rolling-art-gallery-{brand}-1.webp → hero + thumb 1 (they show
  //                                        the same view, so same src)
  //   rolling-art-gallery-{brand}-2.webp → thumb 2
  //   rolling-art-gallery-{brand}-3.webp → thumb 3
  //   …and so on
  //
  // CRITICAL: video thumbs must be excluded from the indexing pass —
  // otherwise the video's slot consumes an index number and every
  // subsequent visible thumb ends up pointing at the WRONG branded
  // filename. Video slots are hidden entirely on branded visits (see
  // hideVideoSlots), so skipping them from the indexer is safe.
  function runSwap() {
    // Hide video slots FIRST so the indexer below doesn't count them.
    hideVideoSlots();

    var hero = document.querySelector('[data-sp-hero]');

    // Collect all thumb imgs, deduped, and filter out any video thumbs
    // (or thumbs whose enclosing element is now display:none). This
    // ensures index N in the loop below corresponds to the Nth thumb
    // the customer actually sees in the strip.
    var thumbImgs = [];
    var seen = new Set ? new Set() : { has: function(){return false;}, add: function(){} };
    document.querySelectorAll('[data-sp-thumb]').forEach(function (t) {
      // Reject video thumbs — they're hidden and their slot should not
      // consume a branded-file index.
      if (t.classList && t.classList.contains('sp-thumb--video')) return;
      if (t.getAttribute('data-media-type') === 'video') return;
      // Reject any thumb whose ancestor was display:none'd (defensive —
      // covers hero-video panes and similar).
      if (t.offsetParent === null && t.style.display === 'none') return;

      var img = t.tagName === 'IMG' ? t : t.querySelector('img');
      if (img && !seen.has(img)) {
        seen.add(img);
        thumbImgs.push(img);
      }
    });

    if (hero) swapImage(hero, 1);
    thumbImgs.forEach(function (img, i) {
      // Cap the numbered swap at NUMBERED_THUMB_COUNT so we don't
      // spam the console with 404s for slots that never had a
      // branded upload. Anything beyond that count is a variant
      // colour/size thumb handled below.
      if (i + 1 > NUMBERED_THUMB_COUNT) return;
      swapImage(img, i + 1);
    });
    console.log('[rolling-art] Brand gallery swap: ' + brand +
      ' — hero + ' + Math.min(thumbImgs.length, NUMBERED_THUMB_COUNT) +
      ' numbered thumbnails');

    // Colour variant thumbs — their .sp-thumb-base is baked from the
    // hero at Liquid render time (still Ferrari). Point it at the
    // branded hero URL so the coloured overlay sits on a Porsche mug.
    swapColourVariantThumbs();

    // Size variant thumbs (Latte + Travel Mug) — each has its own
    // branded upload convention: rolling-art-gallery-{brand}-latte.webp
    // and rolling-art-gallery-{brand}-travelmug.webp. Falls back to
    // the original size composite on 404.
    swapSizeVariantThumbs();

    // Size hero URLs — the ACTUAL hero that shows when the customer
    // clicks the Latte or Travel Mug variant. mug-color-overlay.js
    // reads data-{size}-hero-url attributes and applies them on
    // variant change, so we rewrite those to point at the branded
    // uploads. Without this, selecting Travel Mug loads Ferrari.
    rewireSizeHeroUrls();

    // Video slot: async — either wire the branded MP4 or keep the
    // slot hidden. Runs after the image swap so the customer never
    // sees a flash of the wrong video during the check.
    wireBrandedVideo();

    // Real customer mugs strip (above intake heading). Hidden by
    // default; reveal only when a branded photo exists in Shopify
    // Files under rolling-art-real-mugs-{brand}.webp. Uploading a
    // new brand's real-mugs shot activates the section — no code
    // changes needed for future brands.
    wireBrandedRealMugs();
  }

  // Probe for a branded real-mugs photo and, if it loads, drop it
  // into the intake's data-ra-real-mugs section (over whatever the
  // default probe placed there). Failure is silent — the default
  // real-mugs strip stays visible from the intake snippet's own
  // always-on probe (see rolling-art-intake.liquid init block).
  function wireBrandedRealMugs() {
    var section = document.querySelector('[data-ra-real-mugs]');
    if (!section) return;
    var img = section.querySelector('[data-ra-real-mugs-img]');
    if (!img) return;
    var url = SHOPIFY_FILES_BASE + 'rolling-art-real-mugs-' + brand + '.webp';
    var probe = new Image();
    probe.onload = function () {
      img.src = url;
      section.style.display = '';
      console.log('[rolling-art] Branded real-mugs photo revealed: ' + url);
    };
    probe.onerror = function () {
      console.log('[rolling-art] No branded real-mugs photo for ' + brand +
                  ' — falling back to default (or hidden if that missing too).');
    };
    probe.src = url;
  }

  // Rewrite the .sp-thumb-base <img> inside every colour variant thumb
  // to the branded hero URL. The overlay (colour tint) sits on top and
  // takes care of the visual per-colour differentiation. Also rewrites
  // the thumb's data-hero-src so click-to-promote goes to the branded
  // hero, not the original Ferrari.
  function swapColourVariantThumbs() {
    var branded = brandedUrl(1);
    var thumbs = document.querySelectorAll(
      '.sp-thumb--variant[data-variant-thumb-dim="colour"]');
    if (!thumbs.length) return;
    thumbs.forEach(function (t) {
      var base = t.querySelector('.sp-thumb-base');
      if (base) {
        base.removeAttribute('srcset');
        base.src = branded;
      }
      t.setAttribute('data-hero-src', branded);
      t.removeAttribute('data-hero-srcset');
    });
    console.log('[rolling-art] Colour variant thumbs pointed at ' + branded +
                ' (' + thumbs.length + ' thumbs)');
  }

  // Latte / Travel Mug size variant thumbs — TWO things need swapping
  // for each size when a brand is active:
  //
  //   1. The tiny thumbnail in the variant strip (this function).
  //      That's the .sp-thumb--variant[data-variant-thumb-dim="size"]
  //      elements rendered by the section for the NON-selected sizes.
  //
  //   2. The FULL HERO image the customer sees after they select the
  //      Latte or Travel Mug variant (rewireSizeHeroUrls below). The
  //      section stamps data-latte-hero-url + data-travelmug-hero-url
  //      onto a wrapper element (via mug-color-overlay.liquid) and
  //      mug-color-overlay.js reads those to swap the hero on variant
  //      change. Without rewriting them, selecting Travel Mug on a
  //      ?brand=porsche page loads the default Ferrari travelmug file.
  //
  // Both point at the same branded upload:
  //   rolling-art-gallery-{brand}-latte.webp     — for latte
  //   rolling-art-gallery-{brand}-travelmug.webp — for travel mug
  function swapSizeVariantThumbs() {
    var thumbs = document.querySelectorAll(
      '.sp-thumb--variant[data-variant-thumb-dim="size"]');
    thumbs.forEach(function (t) {
      var value = (t.getAttribute('data-variant-thumb-value') || '').toLowerCase();
      var key = null;
      if (value.indexOf('latte')  >= 0) key = 'latte';
      if (value.indexOf('travel') >= 0) key = 'travelmug';
      if (!key) return;
      var branded = SHOPIFY_FILES_BASE + 'rolling-art-gallery-' + brand + '-' +
                    key + '.webp';
      var img = t.querySelector('.sp-thumb-base') || t.querySelector('img');
      if (!img) return;
      var originalSrc = img.getAttribute('src') || '';
      var originalHeroSrc = t.getAttribute('data-hero-src') || '';
      img.onerror = function () {
        img.onerror = null;
        img.src = originalSrc;
        // Restore the click-target too so a tap doesn't promote a
        // non-existent branded URL into the hero.
        if (originalHeroSrc) t.setAttribute('data-hero-src', originalHeroSrc);
      };
      img.removeAttribute('srcset');
      img.src = branded;
      t.setAttribute('data-hero-src', branded);
    });
  }

  // Rewrite data-latte-hero-url and data-travelmug-hero-url on any
  // element that carries them. These attributes are what mug-color-
  // overlay.js reads to swap the FULL hero image when the customer
  // selects the Latte or Travel Mug size variant — so without this,
  // selecting Travel Mug on a branded page loads the default Ferrari
  // travelmug composite (the file matching the product handle).
  function rewireSizeHeroUrls() {
    var lattedBranded = SHOPIFY_FILES_BASE + 'rolling-art-gallery-' + brand + '-latte.webp';
    var travelBranded = SHOPIFY_FILES_BASE + 'rolling-art-gallery-' + brand + '-travelmug.webp';
    // mug-color-overlay.js reads the size-hero URLs from TWO different
    // element kinds depending on which flow fires:
    //   • [data-fb-photo-default-overlay] — the fb-photo wrapper; used
    //     by syncPersonalisedDefaultOverlay() to swap the img.src on
    //     the default-overlay wrapper itself.
    //   • [data-mug-overlay]              — the colour-tint overlay;
    //     used by the main variant:change handler (pickHeroUrlForSize
    //     reads overlays[0].dataset.latteHeroUrl to swap the HERO
    //     image on Latte/Travel Mug selection).
    // Set the branded URLs on BOTH so whichever handler fires picks
    // them up. Also unconditional — Liquid only emits the attribute
    // when the default {handle}.webp / travelmug-{handle}.webp file
    // exists in Shopify Files, so if either default has been deleted
    // or hasn't been uploaded, the attribute would be missing and a
    // querySelectorAll('[data-latte-hero-url]') would find nothing.
    var selectors = [
      '[data-fb-photo-default-overlay]',
      '[data-mug-overlay]',
    ];
    var total = 0;
    selectors.forEach(function (sel) {
      var els = document.querySelectorAll(sel);
      els.forEach(function (el) {
        el.setAttribute('data-latte-hero-url',    lattedBranded);
        el.setAttribute('data-travelmug-hero-url', travelBranded);
        total++;
      });
    });
    console.log('[rolling-art] Size hero URLs rewired on ' + total +
                ' overlay elements (fb-photo + mug-overlay): latte → ' + lattedBranded +
                ', travel → ' + travelBranded);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSwap);
  } else {
    runSwap();
  }
})();
